import React, { useEffect, useMemo, useState } from 'react';
import {
  useGetIdentity,
  useGetMany,
  useNotify,
  useRefresh,
} from 'react-admin';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { getToken } from '../authentication/authProvider';
import {
  DEFAULT_AMOUNT_CENTS,
  FuneralCoronerRow,
} from './CoronerPaymentModal';
import {
  coronerRowAddress,
  formatCoronerAddress,
  parseCoronerAddress,
} from '../coroners/coronerAddress';

const NEST_API_URL =
  process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001/';

type RowStatus = 'idle' | 'sending' | 'paid' | 'failed';

export function CoronerPayAllModal({
  open,
  onClose,
  unpaidRows,
}: {
  open: boolean;
  onClose: () => void;
  unpaidRows: FuneralCoronerRow[];
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();

  const [amountDollars, setAmountDollars] = useState<string>(
    String(DEFAULT_AMOUNT_CENTS / 100),
  );
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [statuses, setStatuses] = useState<Record<number, RowStatus>>({});
  const [sending, setSending] = useState(false);

  // Look up funerals → deceased clients in one batch each.
  const funeralIds = useMemo(
    () => Array.from(new Set(unpaidRows.map((r) => r.funeral_id))),
    [unpaidRows],
  );
  const { data: funerals = [] } = useGetMany<any>('funerals', { ids: funeralIds });
  const deceasedClientIds = useMemo(
    () =>
      Array.from(
        new Set(
          (funerals as any[])
            .map((f) => f?.deceased_client_id)
            .filter(Boolean) as number[],
        ),
      ),
    [funerals],
  );
  const { data: clients = [] } = useGetMany<any>('clients', { ids: deceasedClientIds });

  // Same priority as the backend: the coroner's current record, else the
  // funeral's snapshot. Shown per row so the unit can be checked before sending.
  const coronerIds = useMemo(
    () => Array.from(new Set(unpaidRows.map((r) => r.coroner_id).filter(Boolean))),
    [unpaidRows],
  );
  const { data: coroners = [], isLoading: coronersLoading } = useGetMany<any>('coroners', {
    ids: coronerIds,
  });
  const addressByRowId = useMemo(() => {
    const coronerById = Object.fromEntries((coroners as any[]).map((c) => [c.id, c]));
    const out: Record<number, string | null> = {};
    for (const r of unpaidRows) {
      const c = coronerById[r.coroner_id];
      const addr =
        (c && coronerRowAddress(c)) ||
        coronerRowAddress({
          address: r.coroner_address_snapshot,
          address_line1: r.coroner_address_line1,
          address_line2: r.coroner_address_line2,
          city: r.coroner_city,
          province: r.coroner_province,
          postal_code: r.coroner_postal_code,
        }) ||
        parseCoronerAddress(r.coroner_address_snapshot);
      const complete =
        addr && addr.addressLine1 && addr.city && addr.provinceOrState && addr.postalOrZip;
      out[r.id] = complete ? formatCoronerAddress(addr) : null;
    }
    return out;
  }, [coroners, unpaidRows]);

  const funeralById = useMemo(
    () => Object.fromEntries((funerals as any[]).map((f) => [f.id, f])),
    [funerals],
  );
  const clientById = useMemo(
    () => Object.fromEntries((clients as any[]).map((c) => [c.id, c])),
    [clients],
  );

  // On open, reset selection (all rows with a complete address checked) +
  // statuses + amount.
  useEffect(() => {
    if (!open || coronersLoading) return;
    const sel: Record<number, boolean> = {};
    for (const r of unpaidRows) sel[r.id] = !!addressByRowId[r.id];
    setSelected(sel);
    setStatuses({});
    setAmountDollars(String(DEFAULT_AMOUNT_CENTS / 100));
  }, [open, unpaidRows, coronersLoading, addressByRowId]);

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const handleSendAll = async () => {
    const cents = Math.round(Number(amountDollars) * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      notify('Enter a valid amount', { type: 'warning' });
      return;
    }
    setSending(true);
    const token = await getToken();
    const targets = unpaidRows.filter((r) => selected[r.id]);
    let okCount = 0, failCount = 0;
    // Run in small concurrent batches to be polite to the API.
    const concurrency = 4;
    let cursor = 0;
    const runOne = async (row: FuneralCoronerRow) => {
      setStatuses((s) => ({ ...s, [row.id]: 'sending' }));
      try {
        const res = await fetch(`${NEST_API_URL}coroners/payments/${row.id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amountCents: cents,
            assignedByUserId: numericId((identity as any)?.id),
            assignedByUserName: (identity as any)?.fullName ?? (identity as any)?.email ?? null,
          }),
        });
        const body = await res.json().catch(() => ({}));
        if (res.ok && body?.paymentStatus === 'paid') {
          okCount++;
          setStatuses((s) => ({ ...s, [row.id]: 'paid' }));
        } else {
          failCount++;
          setStatuses((s) => ({ ...s, [row.id]: 'failed' }));
        }
      } catch {
        failCount++;
        setStatuses((s) => ({ ...s, [row.id]: 'failed' }));
      }
    };
    const workers: Promise<void>[] = [];
    const next = async () => {
      while (cursor < targets.length) {
        const i = cursor++;
        await runOne(targets[i]);
      }
    };
    for (let i = 0; i < concurrency; i++) workers.push(next());
    await Promise.all(workers);
    setSending(false);
    notify(`${okCount} sent, ${failCount} failed`, {
      type: failCount ? 'warning' : 'success',
    });
    refresh();
  };

  const allDone = unpaidRows.length > 0 && unpaidRows.every((r) => {
    const s = statuses[r.id];
    return s === 'paid' || s === 'failed' || !selected[r.id];
  });

  return (
    <Dialog
      open={open}
      onClose={() => !sending && onClose()}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Pay all unpaid coroner cheques ({selectedCount} selected)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ my: 1 }}>
          <TextField
            label="Amount per cheque"
            type="number"
            size="small"
            value={amountDollars}
            onChange={(e) => setAmountDollars(e.target.value)}
            InputProps={{ startAdornment: <Box sx={{ pr: 0.5 }}>$</Box> }}
            inputProps={{ step: '0.01', min: '0' }}
            sx={{ maxWidth: 220 }}
          />
          <Box sx={{ maxHeight: 420, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedCount > 0 && selectedCount < unpaidRows.length}
                      checked={selectedCount === unpaidRows.length && unpaidRows.length > 0}
                      onChange={(e) => {
                        const v = e.target.checked;
                        const sel: Record<number, boolean> = {};
                        for (const r of unpaidRows) sel[r.id] = v && !!addressByRowId[r.id];
                        setSelected(sel);
                      }}
                      disabled={sending}
                    />
                  </TableCell>
                  <TableCell>Funeral</TableCell>
                  <TableCell>Deceased</TableCell>
                  <TableCell>Coroner</TableCell>
                  <TableCell>Mailing address</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unpaidRows.map((r) => {
                  const f = funeralById[r.funeral_id];
                  const c = f?.deceased_client_id ? clientById[f.deceased_client_id] : null;
                  const deceased = c ? `${c.last_name ?? ''}, ${c.first_name ?? ''}`.replace(/^,\s*|,\s*$/g, '') : '…';
                  const status = statuses[r.id] ?? 'idle';
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={!!selected[r.id]}
                          onChange={(e) => setSelected((s) => ({ ...s, [r.id]: e.target.checked }))}
                          disabled={sending || status !== 'idle' || !addressByRowId[r.id]}
                        />
                      </TableCell>
                      <TableCell>{r.funeral_id}</TableCell>
                      <TableCell>{deceased}</TableCell>
                      <TableCell>{r.coroner_name_snapshot}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {addressByRowId[r.id] ?? (
                          <Box component="span" sx={{ color: 'error.main' }}>
                            Needs address — pay individually
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <RowStatusChip status={status} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending && !allDone}>
          {allDone ? 'Close' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSendAll()}
          disabled={sending || selectedCount === 0}
        >
          {sending ? <CircularProgress size={20} /> : `Send ${selectedCount} cheque${selectedCount === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function numericId(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function RowStatusChip({ status }: { status: RowStatus }) {
  const map = {
    idle: { label: 'Pending', sx: { bgcolor: 'grey.100', color: 'grey.700' } },
    sending: { label: 'Sending…', sx: { bgcolor: 'info.50', color: 'info.700' } },
    paid: { label: 'Paid', sx: { bgcolor: 'success.50', color: 'success.700' } },
    failed: { label: 'Failed', sx: { bgcolor: 'error.50', color: 'error.700' } },
  } as const;
  const { label, sx } = map[status];
  return (
    <Box
      sx={{
        display: 'inline-block',
        px: 1, py: 0.25,
        borderRadius: 1,
        fontSize: 11,
        fontWeight: 500,
        ...sx,
      }}
    >
      {label}
    </Box>
  );
}
